import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { logger } from '@/utils/structuredLogging';

// Helper to delay execution
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export function useAdminRole() {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkAdminRole() {
      logger.info('Starting admin check', { 
        component: 'useAdminRole',
        action: 'check',
        metadata: {
          hasUser: !!user, 
          userId: user?.id,
          authLoading 
        }
      });

      // Wait for auth to finish loading
      if (authLoading) {
        logger.info('Auth still loading, waiting', { 
          component: 'useAdminRole',
          action: 'wait'
        });
        return;
      }

      if (!user) {
        logger.info('No user found after auth loaded', { 
          component: 'useAdminRole',
          action: 'noUser'
        });
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      // Retry logic with exponential backoff
      const maxRetries = 3;
      const retryDelays = [200, 500, 1000];
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          logger.info(`Checking admin role (attempt ${attempt + 1}/${maxRetries})`, {
            component: 'useAdminRole',
            action: 'checkRole',
            metadata: { userId: user.id, attempt: attempt + 1 }
          });
          
          const { data, error } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .eq('role', 'admin')
            .maybeSingle(); // Use maybeSingle() instead of single() to handle no rows gracefully

          if (error) {
            logger.error(`Admin check database error (attempt ${attempt + 1})`, {
              component: 'useAdminRole',
              action: 'checkRole',
              metadata: { userId: user.id, attempt: attempt + 1 }
            }, error);
            
            // If this is the last attempt, log and fail
            if (attempt === maxRetries - 1) {
              setIsAdmin(false);
              setLoading(false);
              return;
            }
            
            // Wait before retrying
            await delay(retryDelays[attempt]);
            continue;
          }

          // Success - we got a response
          const isUserAdmin = !!data;
          logger.info('Admin check successful', {
            component: 'useAdminRole',
            action: 'checkRole',
            metadata: { isAdmin: isUserAdmin, userId: user.id }
          });
          setIsAdmin(isUserAdmin);
          setLoading(false);
          return;

        } catch (error) {
          logger.error(`Admin check unexpected error (attempt ${attempt + 1})`, {
            component: 'useAdminRole',
            action: 'checkRole',
            metadata: { userId: user.id, attempt: attempt + 1 }
          }, error as Error);
          
          if (attempt === maxRetries - 1) {
            setIsAdmin(false);
            setLoading(false);
            return;
          }
          
          await delay(retryDelays[attempt]);
        }
      }
    }

    checkAdminRole();
  }, [user, authLoading]);

  return { isAdmin, loading };
}